import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { RefreshCw } from 'lucide-react';
import { limit, orderBy, where } from 'firebase/firestore';
import {
  canOrg,
  canProject,
  type DevPreferenceField,
  type DocumentPointer,
  type DocumentVersion,
  type Job,
  type Questionnaire as QuestionnaireDoc,
  type QuestionnaireAnswer,
  type TddSection,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { useOrgs } from '../orgs/OrgProvider.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { MarkdownEditor } from '../../components/ui/MarkdownEditor.js';
import { Term } from '../../components/ui/Term.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover.js';
import { ExportMenu } from '../exports/ExportMenu.js';
import {
  Questionnaire,
  QuestionnaireActions,
  QuestionnaireChoice,
  QuestionnaireChoiceDescription,
  QuestionnaireChoices,
  QuestionnaireDescription,
  QuestionnaireError,
  QuestionnaireInput,
  QuestionnaireItem,
  QuestionnaireNext,
  QuestionnairePrevious,
  QuestionnaireProgress,
  QuestionnaireSubmit,
  QuestionnaireTitle,
} from '../../components/ui/Questionnaire.js';

/**
 * Lets an org admin turn a just-typed/selected questionnaire answer into the org's saved dev
 * default, without leaving the questionnaire. The form is an uncontrolled multi-step wizard (see
 * Questionnaire.tsx), so there's no React state holding "the current value of this question" —
 * this reads it straight from the enclosing native <form> via FormData at click time instead,
 * the same way the final submit handler does.
 */
function SaveAsOrgDefaultButton({
  orgId,
  field,
  questionId,
}: {
  orgId: string;
  field: DevPreferenceField;
  questionId: string;
}) {
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  async function handleClick(e: MouseEvent<HTMLButtonElement>) {
    const form = e.currentTarget.closest('form');
    const value = (form ? new FormData(form).get(questionId) : null) as string | null;
    if (!value) return;
    setStatus('saving');
    try {
      await api.put(`/orgs/${orgId}/settings/dev-preferences`, { [field]: value });
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="self-start"
      disabled={status === 'saving'}
      onClick={(e) => void handleClick(e)}
    >
      {status === 'saved'
        ? 'Saved as org default ✓'
        : status === 'error'
          ? 'Failed — retry'
          : status === 'saving'
            ? 'Saving…'
            : 'Save as org default'}
    </Button>
  );
}

export function TddPage() {
  const { projectId, project, role } = useProject();
  const { orgs } = useOrgs();
  const orgRole = project ? orgs.find((o) => o.id === project.orgId)?.role : undefined;
  const canManageOrg = canOrg(orgRole, 'org.update');
  const canAccept = canProject(role, 'document.accept');
  const { data: prdPointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/prd`);
  const { data: tddPointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/tdd`);
  const { data: currentVersion } = useDocument<DocumentVersion>(
    tddPointer?.currentVersionId
      ? `projects/${projectId}/documents/tdd/versions/${tddPointer.currentVersionId}`
      : null,
  );

  const [sections, setSections] = useState<TddSection[] | null>(null);
  // The sections as of the last saved (or loaded) version, and that version's id. Every save
  // creates a new version, so this is what stops an unchanged draft being saved again — accept
  // used to save unconditionally, turning the first accepted TDD into v2. Edits replace the
  // `sections` array, so a reference comparison is enough to spot unsaved changes.
  const [saved, setSaved] = useState<{ sections: TddSection[]; versionId: string } | null>(null);
  const dirty = sections !== null && sections !== saved?.sections;
  const [jobId, setJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  // True while the user is redoing the questionnaire over an existing draft ("Change answers"),
  // so the questionnaire shows even though `sections` is already populated.
  const [redoing, setRedoing] = useState(false);

  // The questionnaire is persisted the moment it's generated (see llm.ts), so it's read live
  // from Firestore rather than mirrored from the job result — that way an in-progress
  // questionnaire survives a page refresh instead of vanishing with the local component state.
  const questionnaireConstraints = useMemo(
    () => [where('completedAt', '==', null), orderBy('createdAt', 'desc'), limit(1)],
    [],
  );
  const { data: openQuestionnaires } = useCollection<QuestionnaireDoc>(
    projectId ? `projects/${projectId}/questionnaires` : null,
    questionnaireConstraints,
  );
  const questionnaire = openQuestionnaires[0] ?? null;

  // Minimal shape the Questionnaire primitive needs to drive validation/navigation — the actual
  // prompt/description/choice labels are rendered separately as children below.
  const questionnaireItems = useMemo(
    () =>
      questionnaire?.questions.map((q) => ({
        name: q.id,
        required: false,
        choices: q.suggestedOptions.map((opt) => ({ value: opt.label })),
      })) ?? [],
    [questionnaire],
  );

  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);

  useEffect(() => {
    if (!job) return;
    if (job.status === 'succeeded' && job.result) {
      if (job.type === 'tdd_generate') {
        setSections(job.result as unknown as TddSection[]);
        setRedoing(false);
      }
      setJobId(null);
    } else if (job.status === 'failed') {
      setError(job.error ?? 'Generation failed');
      setJobId(null);
    }
  }, [job]);

  // The version `saved` describes. A save's response and the live listener reporting the new
  // version can arrive in either order; whichever comes first records it and the other is a no-op.
  const savedVersionIdRef = useRef<string | null>(null);

  // Seed from the current version — but not when it's the version this page just saved, so
  // edits typed while that save was in flight aren't overwritten by the live listener.
  useEffect(() => {
    if (!currentVersion || savedVersionIdRef.current === currentVersion.id) return;
    savedVersionIdRef.current = currentVersion.id;
    const loaded = (currentVersion.content as { sections: TddSection[] }).sections;
    setSections(loaded);
    setSaved({ sections: loaded, versionId: currentVersion.id });
  }, [currentVersion]);

  async function startQuestionnaire() {
    setBusy(true);
    setError(null);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/tdd/questionnaire`,
        {},
      );
      setJobId(id);
    } catch {
      setError('Could not start the questionnaire.');
    } finally {
      setBusy(false);
    }
  }

  /** Regenerates from the last completed questionnaire's answers, replacing the on-screen draft.
   * Saved versions are untouched until the user saves. */
  async function regenerateSameAnswers() {
    setRegenOpen(false);
    if (dirty && !window.confirm('Regenerating replaces your unsaved edits. Continue?')) return;
    setBusy(true);
    setError(null);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/tdd/generate`,
        {},
      );
      setJobId(id);
    } catch {
      setError('Could not regenerate the TDD.');
    } finally {
      setBusy(false);
    }
  }

  /** Starts a fresh questionnaire so the answers can be changed before generating again. */
  async function regenerateNewAnswers() {
    setRegenOpen(false);
    if (dirty && !window.confirm('Regenerating replaces your unsaved edits. Continue?')) return;
    setRedoing(true);
    await startQuestionnaire();
  }

  async function submitAnswers(answers: QuestionnaireAnswer[]) {
    if (!questionnaire) return;
    setBusy(true);
    setError(null);
    try {
      await api.put(`/projects/${projectId}/tdd/questionnaire/${questionnaire.id}`, { answers });
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/tdd/generate`,
        {},
      );
      setJobId(id);
    } catch {
      setError('Could not generate the TDD.');
    } finally {
      setBusy(false);
    }
  }

  /** Saves the current sections as a new version and returns its id. */
  async function saveVersion(): Promise<string> {
    const toSave = sections!;
    const { versionId } = await api.put<{ versionId: string }>(`/projects/${projectId}/tdd`, {
      sections: toSave,
    });
    if (savedVersionIdRef.current !== versionId) {
      savedVersionIdRef.current = versionId;
      setSaved({ sections: toSave, versionId });
    }
    return versionId;
  }

  async function saveDraft() {
    if (!sections || !dirty) return;
    setBusy(true);
    setError(null);
    try {
      await saveVersion();
    } catch {
      setError('Could not save the draft.');
    } finally {
      setBusy(false);
    }
  }

  function handleQuestionnaireSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!questionnaire) return;
    const formData = new FormData(event.currentTarget);
    const answers = questionnaire.questions.map((q) => ({
      questionId: q.id,
      selectedOptionLabel: (formData.get(q.id) as string | null) || undefined,
    }));
    void submitAnswers(answers);
  }

  /** Accepts exactly what's on screen: the version already saved when nothing has changed, or a
   * freshly saved one when there are unsaved edits. */
  async function accept() {
    if (!sections) return;
    setBusy(true);
    setError(null);
    try {
      const versionId = dirty || !saved ? await saveVersion() : saved.versionId;
      await api.post(`/projects/${projectId}/tdd/accept`, { versionId });
    } catch {
      setError('Could not accept this version.');
    } finally {
      setBusy(false);
    }
  }

  if (prdPointer?.status !== 'accepted') {
    return (
      <div className="p-4 sm:p-6">
        <Card className="p-6 text-center text-sm text-muted sm:p-8">
          Accept the <Term id="prd" /> before generating a <Term id="tdd" />.
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      {/* Sticky only from md: on a phone the wrapped toolbar would eat most of the screen once the
          on-screen keyboard is up. */}
      <div className="flex flex-col gap-3 border-b border-border bg-bg py-3 sm:flex-row sm:items-center md:sticky md:top-0 md:z-10">
        <div>
          <h1 className="text-lg font-semibold">
            <Term id="tdd" />
          </h1>
          <p className="text-sm text-muted">
            {tddPointer?.status === 'accepted'
              ? `Accepted — version ${tddPointer.currentVersionNumber}`
              : tddPointer?.status === 'draft'
                ? 'Draft — not yet accepted'
                : 'Not started'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:justify-end">
          {tddPointer?.currentVersionId && <ExportMenu projectId={projectId} docType="tdd" />}
          {tddPointer && tddPointer.currentVersionNumber > 1 && (
            <Link to={`/projects/${projectId}/documents/tdd/history`}>
              <Button type="button">History</Button>
            </Link>
          )}
          {!sections && !questionnaire && !jobId && (
            <Button variant="default" disabled={busy} onClick={() => void startQuestionnaire()}>
              Start
            </Button>
          )}
          {sections && !redoing && (
            <>
              <Popover open={regenOpen} onOpenChange={setRegenOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" disabled={busy || !!jobId}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Regenerate
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="flex w-60 flex-col gap-0.5 p-1">
                  <button
                    type="button"
                    onClick={() => void regenerateSameAnswers()}
                    className="rounded-md px-2.5 py-2 text-left text-sm text-text hover:bg-surface-2"
                  >
                    Same answers
                    <span className="block text-xs text-muted">Re-run with the last answers</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void regenerateNewAnswers()}
                    className="rounded-md px-2.5 py-2 text-left text-sm text-text hover:bg-surface-2"
                  >
                    Change answers
                    <span className="block text-xs text-muted">Redo the questionnaire</span>
                  </button>
                </PopoverContent>
              </Popover>
              <Button disabled={busy || !dirty} onClick={() => void saveDraft()}>
                Save draft
              </Button>
              {canAccept ? (
                <Button
                  variant="default"
                  disabled={busy || (tddPointer?.status === 'accepted' && !dirty)}
                  onClick={() => void accept()}
                >
                  {dirty ? 'Save & accept' : 'Accept'}
                </Button>
              ) : (
                <span className="text-xs text-muted">A project lead or owner accepts versions.</span>
              )}
            </>
          )}
        </div>
      </div>

      {jobId && <Card className="p-4 text-sm text-muted">Working…</Card>}
      {error && <Card className="border-danger p-4 text-sm text-danger">{error}</Card>}

      {questionnaire && (!sections || redoing) && (
        <Card className="p-4 sm:p-6">
          <Questionnaire
            items={questionnaireItems}
            onSubmit={handleQuestionnaireSubmit}
            className="gap-6"
          >
            <QuestionnaireProgress />
            {questionnaire.questions.map((q) => (
              <QuestionnaireItem key={q.id} name={q.id}>
                <QuestionnaireTitle>{q.prompt}</QuestionnaireTitle>
                {q.helpText && <QuestionnaireDescription>{q.helpText}</QuestionnaireDescription>}
                <QuestionnaireChoices>
                  {q.suggestedOptions.map((opt) => (
                    <QuestionnaireChoice key={opt.label} value={opt.label}>
                      <span className="font-medium">{opt.label}</span>
                      {opt.description && (
                        <QuestionnaireChoiceDescription>
                          {opt.description}
                        </QuestionnaireChoiceDescription>
                      )}
                    </QuestionnaireChoice>
                  ))}
                  <QuestionnaireInput placeholder="or type your own answer" />
                </QuestionnaireChoices>
                <QuestionnaireError />
                {canManageOrg && q.devPreferenceField && project && (
                  <SaveAsOrgDefaultButton
                    orgId={project.orgId}
                    field={q.devPreferenceField}
                    questionId={q.id}
                  />
                )}
              </QuestionnaireItem>
            ))}
            <QuestionnaireActions>
              {redoing && (
                <Button type="button" variant="ghost" onClick={() => setRedoing(false)}>
                  Cancel
                </Button>
              )}
              <QuestionnairePrevious />
              <QuestionnaireNext />
              <QuestionnaireSubmit disabled={busy}>
                {busy ? 'Working…' : 'Generate TDD'}
              </QuestionnaireSubmit>
            </QuestionnaireActions>
          </Questionnaire>
        </Card>
      )}

      {sections && !(redoing && questionnaire) && (
        <div className="flex flex-col gap-4">
          {sections.map((section, i) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, delay: i * 0.03 }}
            >
              <Card className="flex flex-col gap-2 p-4 sm:p-5">
                <Input
                  value={section.title}
                  onChange={(e) =>
                    setSections((s) =>
                      s!.map((x, xi) => (xi === i ? { ...x, title: e.target.value } : x)),
                    )
                  }
                  className="h-auto border-none bg-transparent text-sm font-semibold"
                />
                <MarkdownEditor
                  rows={10}
                  value={section.body}
                  onChange={(value) =>
                    setSections((s) => s!.map((x, xi) => (xi === i ? { ...x, body: value } : x)))
                  }
                />
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
